import { createContext, useContext } from 'react';
import { useActionTrigger, useErrorNotify } from '../adapters';
import { contentTypes } from '../models/kentico';
import { useDeliveryClient } from './deliveryClient';
import { contentDataSelector } from '../redux/selectors';
import { renderMustacheAndParseAsJSON } from '../helpers/content';
import { useAppSelector } from '../redux/hooks';
/**
 * Hook that provides a function to trigger kentico action objects. handles error situations from
 * bad content parsing, or from erronously named actions
 */
export function useEvaluateAction() {
    const actionTrigger = useActionTrigger();
    const contentData = useAppSelector(contentDataSelector);
    const catchJSONParseErrors = useContentErrorCatcher(SyntaxError);
    const catchAllErrors = useContentErrorCatcher();
    return (action) => {
        catchJSONParseErrors(action, 'Failed to parse Action argument', () => {
            const argument = parseActionArg(action, contentData);
            // triggering an action calls into the parent code (parabola), so anything could happen here
            // so we are using a generic tri
            const actionName = action.elements.action_name.value;
            catchAllErrors(action, `Failed while executing Action ${actionName}`, () => {
                actionTrigger(actionName, argument);
            });
        });
    };
}
function parseActionArg(action, contentData) {
    const rawArg = action.elements.argument.value;
    if (rawArg === '') {
        // Arguments get specificed in a string element in Kentico. If the user leave the element blank,
        // then the API serves it back to us as an empty string.  We need to interpret that as "no
        // argument should be passed"
        return;
    }
    return renderMustacheAndParseAsJSON(rawArg, contentData);
}
/**
 * Hook that provides a function which will swallow up any SyntaxErrors and report them off to the
 * onErrorNotify adapter.
 *
 * The message will have a url to the content in Kentico appended to it, which is why the item
 * is a required argument.
 */
export function useContentErrorCatcher(errorType) {
    const contentErrorNotify = useKenticoContentErrorNotify();
    return (contentItem, errorMessage, func) => {
        try {
            func();
        }
        catch (err) {
            if (!errorType || err instanceof errorType) {
                contentErrorNotify(`${errorMessage}: '${err}'`, { contentItem });
            }
            else {
                throw err;
            }
        }
    };
}
export function useKenticoContentErrorNotify() {
    const errorNotify = useErrorNotify();
    const providerContentItem = useContext(ContentItemContext);
    const urlToContentItem = useUrlToContentItem();
    return (message, options) => {
        const contentItem = (options && options.contentItem) || providerContentItem;
        if (!contentItem) {
            throw new Error(`Cannot determine contentItem to generate url with.

Either pass an item in the options, or use hook within a ContentItemContext.Provider`);
        }
        const wrappedMessage = `Error encountered while processing ${formattedContentTypeName(contentItem.system.type)} from Kentico:

  ${message}

You can edit this content at ${urlToContentItem(contentItem)}`;
        errorNotify(wrappedMessage);
        // Since these messages are useful to see while developing content, we're
        // going to log it to the console as well
        // eslint-disable-next-line no-console
        console.error(wrappedMessage);
    };
}
function formattedContentTypeName(codename) {
    if (!isContentTypeCodename(codename)) {
        throw new Error(`${codename} is not a known content type`);
    }
    return contentTypes[codename].name;
}
function isContentTypeCodename(codename) {
    return codename in contentTypes;
}
function useUrlToContentItem() {
    const [, { environment }] = useDeliveryClient();
    return (item) => `https://app.kontent.ai/${environment.environmentId}/content-inventory/00000000-0000-0000-0000-000000000000/content/${item.system.id}`;
}
export const ContentItemContext = createContext(null);
//# sourceMappingURL=contentErrors.js.map